// Presentational workspace selector. The trigger keeps the sidebar idiom
// (avatar + name + chevron); clicking it opens an app-themed popover menu, not
// the browser's native <select>. Variant 'sidebar-collapsed' shows only the
// avatar circle (used when the sidebar is collapsed to icon-only width), and
// its menu opens to the right of the rail instead of below.
//
// The menu only switches workspaces. Creating a team is not offered here.
//
// Option backgrounds are inline styles on purpose: index.css resets every
// `.sidebar button` to a transparent background, which outranks a utility class.

import React from 'react';

export function getWorkspaceInitials(name) {
    if (!name || typeof name !== 'string') return '?';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

function WorkspaceAvatar({ name, logoUrl, className = '' }) {
    return (
        <span
            className={`sidebar__workspace-avatar ${logoUrl ? 'sidebar__workspace-avatar--has-logo' : ''} ${className}`}
            aria-hidden="true"
        >
            {logoUrl ? (
                <img src={logoUrl} alt="" className="sidebar__workspace-avatar-img" />
            ) : (
                getWorkspaceInitials(name)
            )}
        </span>
    );
}

function WorkspaceSelector({
    workspaces = [],
    selectedWorkspaceId,
    onSelectWorkspace = () => {},
    variant = 'sidebar',
}) {
    const [open, setOpen] = React.useState(false);
    const [activeIdx, setActiveIdx] = React.useState(-1);
    const rootRef = React.useRef(null);
    const triggerRef = React.useRef(null);

    const selected = workspaces.find(ws => ws.id === selectedWorkspaceId);
    const displayName = selected?.name || 'No workspace';
    const logoUrl = selected?.logoUrl || null;
    const isCollapsed = variant === 'sidebar-collapsed';

    React.useEffect(() => {
        if (!open) return undefined;
        const onDocMouseDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener('mousedown', onDocMouseDown);
        return () => window.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);

    // Opening starts arrow-key navigation from the current workspace.
    React.useEffect(() => {
        if (open) setActiveIdx(workspaces.findIndex(ws => ws.id === selectedWorkspaceId));
    }, [open, workspaces, selectedWorkspaceId]);

    if (workspaces.length === 0) return null;

    const close = () => {
        setOpen(false);
        triggerRef.current?.focus();
    };

    const commit = (id) => {
        setOpen(false);
        if (id !== selectedWorkspaceId) onSelectWorkspace(id);
    };

    const handleTriggerKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
        }
    };

    const handleMenuKeyDown = (e) => {
        if (e.key === 'Escape' || e.key === 'Tab') {
            if (e.key === 'Escape') e.preventDefault();
            close();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(i => Math.min(workspaces.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(i => Math.max(0, i - 1));
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (activeIdx >= 0 && workspaces[activeIdx]) commit(workspaces[activeIdx].id);
        }
    };

    // Expanded: under the trigger, inset by the trigger's own 12px side margin.
    // Collapsed: beside the 76px rail, level with the trigger.
    const menuPosition = isCollapsed
        ? 'left-[calc(100%+8px)] top-0 w-64'
        : 'left-3 right-3 top-[calc(100%+6px)]';

    return (
        <div ref={rootRef} className="relative">
            <div
                ref={triggerRef}
                className={`sidebar__workspace ${isCollapsed ? 'sidebar__workspace--collapsed' : ''}`}
                onClick={() => setOpen(o => !o)}
                onKeyDown={handleTriggerKeyDown}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Workspace: ${displayName}. Click to switch.`}
            >
                <WorkspaceAvatar name={displayName} logoUrl={logoUrl} />
                <span className="sidebar__workspace-name" title={displayName}>{displayName}</span>
                <svg
                    className="sidebar__workspace-chevron"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                </svg>
            </div>

            {open && (
                <div
                    role="listbox"
                    aria-label="Select workspace"
                    tabIndex={-1}
                    onKeyDown={handleMenuKeyDown}
                    ref={(node) => { if (node) node.focus(); }}
                    className={`absolute z-50 max-h-[60vh] overflow-y-auto rounded-xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] p-1 shadow-[0_12px_30px_rgba(11,31,24,.18)] focus:outline-none ${menuPosition}`}
                >
                    <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)] select-none">
                        Workspaces
                    </div>
                    {workspaces.map((ws, i) => {
                        const isSelected = ws.id === selectedWorkspaceId;
                        const isActive = i === activeIdx;
                        return (
                            <button
                                key={ws.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => commit(ws.id)}
                                onMouseEnter={() => setActiveIdx(i)}
                                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] transition-colors"
                                style={{
                                    backgroundColor: isActive ? 'var(--surface-subtle)' : 'transparent',
                                    color: 'var(--text-primary)',
                                    fontWeight: isSelected ? 600 : 500,
                                }}
                            >
                                <WorkspaceAvatar name={ws.name} logoUrl={ws.logoUrl || null} />
                                <span className="min-w-0 flex-1 truncate">{ws.name}</span>
                                {isSelected && (
                                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 flex-shrink-0 text-[color:var(--color-primary)]" aria-hidden="true">
                                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default WorkspaceSelector;
